import {HttpClient} from '@angular/common/http';
import {inject, Injectable} from '@angular/core';
import {MetaConcessionaria, MetaDimensao, MetaGrupo, MetaProgresso, MetaProjecao, MetaRequest, MetaResponse} from '@core/models/meta';
import {Notificacao} from '@core/models/notificacoes';
import {environment} from '@environments/environment';
import {map, Observable, of, shareReplay} from 'rxjs';

const DIMENSOES: MetaDimensao[] = ['concessionaria', 'status', 'fase', 'motivo', 'curso'];
const isLocal = (environment as any).local === true;

@Injectable({providedIn: 'root'})
export class MetaService {
  private uri = environment.api.uri.concat('meta');
  private http = inject(HttpClient);
  private mockNotif$ = this.http.get<any[]>('/assets/mock-notificacoes.json').pipe(shareReplay(1));

  private mockMetas: MetaResponse[] = [
    {
      id: 1,
      nome: 'Meta de Bonificação 2025',
      descricao: 'Notificações TAP, EM ANÁLISE e ARBITRAL — acervo total',
      valorMeta: 5_000_000_000,
      dataInicio: '2000-01-01',
      dataFim: '2025-12-31',
      statusContribuintes: ['TAP', 'EM ANÁLISE', 'ARBITRAL'],
      ativo: true,
      tipoMeta: 'VALOR',
      statusAlvo: null,
    },
  ];
  private mockNextId = 2;

  listar(): Observable<MetaResponse[]> {
    if (isLocal) return of([...this.mockMetas]);
    return this.http.get<MetaResponse[]>(this.uri);
  }

  criar(dto: MetaRequest): Observable<MetaResponse> {
    if (isLocal) {
      const nova: MetaResponse = {id: this.mockNextId++, ...dto};
      this.mockMetas.push(nova);
      return of(nova);
    }
    return this.http.post<MetaResponse>(this.uri, dto);
  }

  atualizar(id: number, dto: MetaRequest): Observable<MetaResponse> {
    if (isLocal) {
      const atualizada: MetaResponse = {id, ...dto};
      const idx = this.mockMetas.findIndex(m => m.id === id);
      if (idx >= 0) this.mockMetas[idx] = atualizada; else this.mockMetas.push(atualizada);
      return of(atualizada);
    }
    return this.http.put<MetaResponse>(`${this.uri}/${id}`, dto);
  }

  deletar(id: number): Observable<void> {
    if (isLocal) {
      this.mockMetas = this.mockMetas.filter(m => m.id !== id);
      return of(undefined);
    }
    return this.http.delete<void>(`${this.uri}/${id}`);
  }

  getProgresso(id: number): Observable<MetaProgresso> {
    if (isLocal) return this.getMockProgresso(id);
    return this.http.get<MetaProgresso>(`${this.uri}/${id}/progresso`);
  }

  getMockMetas(): MetaResponse[] {
    return [...this.mockMetas];
  }

  /** Filtra as notificações mock que contribuem para uma meta (mesmo critério do backend: status IN statusContribuintes + período). */
  private filtrarContribuintes(data: any[], meta: MetaResponse): any[] {
    const inicio = new Date(meta.dataInicio);
    const fim = new Date(meta.dataFim + 'T23:59:59');
    return data.filter(n => {
      if (!meta.statusContribuintes.includes(n.status)) return false;
      if (!n.dataNotificacao) return false;
      const d = new Date(n.dataNotificacao + 'T00:00:00');
      return d >= inicio && d <= fim;
    });
  }

  private agrupar(filtered: any[], dimensao: MetaDimensao, valorMeta: number): MetaGrupo[] {
    const byChave = new Map<string, {quantidade: number; valor: number}>();
    for (const n of filtered) {
      const chave = n[dimensao] || '—';
      const e = byChave.get(chave) ?? {quantidade: 0, valor: 0};
      e.quantidade++; e.valor += n.valorAtual || 0;
      byChave.set(chave, e);
    }
    return Array.from(byChave.entries())
      .map(([chave, {quantidade, valor}]) => ({
        chave,
        quantidade,
        valorContribuido: valor,
        percentualDaMeta: valorMeta > 0 ? (valor / valorMeta) * 100 : 0,
      }))
      .sort((a, b) => b.valorContribuido - a.valorContribuido);
  }

  /** Mesmo criterio do backend para PROCESSOS_PENDENTES: status contribuinte + curso alvo + NOT ate o corte (Data Fim). */
  private filtrarPendentes(data: any[], meta: MetaResponse): any[] {
    const corte = new Date(meta.dataFim + 'T23:59:59');
    return data.filter(n =>
      meta.statusContribuintes.includes(n.status) &&
      n.curso === meta.cursoAlvo &&
      n.dataNotificacao && new Date(n.dataNotificacao + 'T00:00:00') <= corte
    );
  }

  /** Entrada = NOT emitida no período, sem filtro de status (usado por TAXA_SAIDA_ENTRADA). */
  private filtrarEntrada(data: any[], meta: MetaResponse): any[] {
    const inicio = new Date(meta.dataInicio);
    const fim = new Date(meta.dataFim + 'T23:59:59');
    return data.filter(n => {
      if (!n.dataNotificacao) return false;
      const d = new Date(n.dataNotificacao + 'T00:00:00');
      return d >= inicio && d <= fim;
    });
  }

  /** Saida = status contribuinte com Andamento no período (usado por TAXA_SAIDA_ENTRADA). */
  private filtrarSaida(data: any[], meta: MetaResponse): any[] {
    const inicio = new Date(meta.dataInicio);
    const fim = new Date(meta.dataFim + 'T23:59:59');
    return data.filter(n => {
      if (!meta.statusContribuintes.includes(n.status)) return false;
      if (!n.andamento) return false;
      const d = new Date(n.andamento + 'T00:00:00');
      return d >= inicio && d <= fim;
    });
  }

  private getMockProgresso(id: number): Observable<MetaProgresso> {
    const meta = this.mockMetas.find(m => m.id === id) ?? this.mockMetas[0];
    const valorMeta = meta.valorMeta;

    return this.mockNotif$.pipe(map(data => {
      const entradaList = meta.tipoMeta === 'TAXA_SAIDA_ENTRADA' ? this.filtrarEntrada(data, meta) : null;
      const filtered = meta.tipoMeta === 'PROCESSOS_PENDENTES'
        ? this.filtrarPendentes(data, meta)
        : meta.tipoMeta === 'TAXA_SAIDA_ENTRADA'
        ? this.filtrarSaida(data, meta)
        : this.filtrarContribuintes(data, meta);
      const total = entradaList ? entradaList.length : filtered.length;
      const diasRestantes = Math.round((new Date(meta.dataFim + 'T23:59:59').getTime() - Date.now()) / 86400000);

      let valorAtingido: number;
      let percentualAtingido: number;
      if (meta.tipoMeta === 'QUANTIDADE' || meta.tipoMeta === 'PROCESSOS_PENDENTES') {
        valorAtingido = total;
        percentualAtingido = valorMeta > 0 ? Math.min(100, (valorAtingido / valorMeta) * 100) : 0;
      } else if (meta.tipoMeta === 'TAXA_SAIDA_ENTRADA') {
        const entrada = entradaList?.length ?? 0;
        const saida = filtered.length;
        const razao = entrada === 0 ? 100 : (saida / entrada) * 100;
        valorAtingido = razao;
        percentualAtingido = valorMeta > 0 ? Math.min(100, (razao / valorMeta) * 100) : 0;
      } else if (meta.tipoMeta === 'MEDIA_DIAS_ABERTO') {
        const dias = filtered
          .filter(n => n.andamento && n.dataNotificacao)
          .map(n => (new Date(n.andamento).getTime() - new Date(n.dataNotificacao).getTime()) / 86400000);
        const media = dias.length > 0 ? dias.reduce((s, d) => s + d, 0) / dias.length : 0;
        valorAtingido = media;
        percentualAtingido = media <= 0 ? 100 : Math.min(100, (valorMeta / media) * 100);
      } else if (meta.tipoMeta === 'PERCENTUAL_STATUS') {
        const countAlvo = filtered.filter(n => n.status === meta.statusAlvo).length;
        const pct = total > 0 ? (countAlvo / total) * 100 : 0;
        valorAtingido = pct;
        percentualAtingido = pct;
      } else {
        valorAtingido = filtered.reduce((s: number, n: any) => s + (n.valorAtual || 0), 0);
        percentualAtingido = valorMeta > 0 ? Math.min(100, (valorAtingido / valorMeta) * 100) : 0;
      }

      const valorRestante = meta.tipoMeta === 'MEDIA_DIAS_ABERTO'
        ? Math.max(0, valorAtingido - valorMeta)
        : Math.max(0, valorMeta - valorAtingido);

      const agrupamentos = Object.fromEntries(DIMENSOES.map(d => [d, this.agrupar(filtered, d, valorMeta)])) as Record<MetaDimensao, MetaGrupo[]>;
      const porConcessionaria: MetaConcessionaria[] = agrupamentos.concessionaria.map(g => ({
        concessionaria: g.chave,
        quantidade: g.quantidade,
        valorContribuido: g.valorContribuido,
        percentualDaMeta: g.percentualDaMeta,
      }));

      const projecao = (meta.tipoMeta === 'VALOR' || meta.tipoMeta === 'QUANTIDADE')
        ? this.calcularProjecaoMock(meta, valorAtingido, valorRestante, percentualAtingido, diasRestantes)
        : null;

      return {
        id: meta.id,
        nome: meta.nome,
        descricao: meta.descricao,
        valorMeta,
        valorAtingido,
        valorRestante,
        percentualAtingido,
        dataInicio: meta.dataInicio,
        dataFim: meta.dataFim,
        diasRestantes,
        totalNotificacoes: total,
        statusContribuintes: meta.statusContribuintes,
        porConcessionaria,
        agrupamentos,
        tipoMeta: meta.tipoMeta,
        statusAlvo: meta.statusAlvo,
        cursoAlvo: meta.cursoAlvo,
        projecao,
      };
    }));
  }

  private calcularProjecaoMock(meta: MetaResponse, valorAtingido: number, valorRestante: number, percentualAtingido: number, diasRestantes: number): MetaProjecao {
    if (percentualAtingido >= 100 || valorRestante <= 0) {
      return {ritmoMedioAtual: 0, projecaoFinal: valorAtingido, percentualProjetado: percentualAtingido, ritmoNecessario: 0, status: 'ATINGIDA'};
    }
    if (diasRestantes < 0) {
      return {ritmoMedioAtual: 0, projecaoFinal: valorAtingido, percentualProjetado: percentualAtingido, ritmoNecessario: null, status: 'ENCERRADA'};
    }
    const diasTotais = Math.round((new Date(meta.dataFim).getTime() - new Date(meta.dataInicio).getTime()) / 86400000);
    const diasDecorridos = Math.max(0, Math.min(diasTotais - diasRestantes, diasTotais));
    const ritmoMedioAtual = diasDecorridos > 0 ? valorAtingido / diasDecorridos : 0;
    const projecaoFinal = valorAtingido + ritmoMedioAtual * diasRestantes;
    const percentualProjetado = meta.valorMeta > 0 ? (projecaoFinal / meta.valorMeta) * 100 : 0;
    const ritmoNecessario = diasRestantes > 0 ? valorRestante / diasRestantes : null;
    return {ritmoMedioAtual, projecaoFinal, percentualProjetado, ritmoNecessario, status: percentualProjetado >= 100 ? 'NO_CAMINHO' : 'FORA_DO_RITMO'};
  }

  /** Drill-down local: filtra as notificações mock por dimensão+chave, sem depender do backend real. */
  getMockNotificacoesFiltradas(progresso: MetaProgresso, dimensao: MetaDimensao, chave: string): Observable<Notificacao[]> {
    return this.mockNotif$.pipe(map(data => {
      const inicio = new Date(progresso.dataInicio);
      const fim = new Date(progresso.dataFim + 'T23:59:59');
      return data.filter(n => {
        if (dimensao === 'status') {
          if (n.status !== chave) return false;
        } else {
          if (!progresso.statusContribuintes.includes(n.status)) return false;
          if (n[dimensao] !== chave) return false;
        }
        if (!n.dataNotificacao) return false;
        const d = new Date(n.dataNotificacao + 'T00:00:00');
        return d >= inicio && d <= fim;
      });
    }));
  }
}
